/**
 * Word-Ribbon indent icons
 *
 * Hand-drawn inline SVGs that replicate Word's actual "Increase Indent" /
 * "Decrease Indent" ribbon glyphs: a four-line paragraph body (full-width
 * top/bottom, indented middle two) plus a filled triangle arrow — pointing
 * right to indent deeper, left to outdent.
 *
 * These are drop-in replacements for the `@fluentui/react-icons`
 * `TextIndentIncrease24Regular` / `TextIndentDecrease24Regular` glyphs, whose
 * thin-chevron arrow and uneven three-line body didn't read as Word's buttons.
 * They accept the same `fontSize` prop and inherit color via `currentColor`, so
 * they slot into `ICON_BY_KIND` alongside the remaining Fluent icons.
 */

import React from "react";

interface IndentIconProps {
  /** Rendered pixel size (width & height). Mirrors Fluent's `fontSize` prop. */
  fontSize?: number | string;
  /** Forwarded so callers can give the glyph an accessible name (role="img"). */
  role?: string;
  /** Accessible name when the glyph carries meaning (paired with role="img"). */
  "aria-label"?: string;
  /** Forwarded so callers can mark the glyph decorative. */
  "aria-hidden"?: React.AriaAttributes["aria-hidden"];
}

const VIEW_BOX = "0 0 24 24";

/**
 * Shared paragraph body — the identical four lines Word draws in both indent
 * buttons: full-width top and bottom, indented (shorter, right-shifted) middle
 * two. Only the arrow differs between increase and decrease.
 */
const ParagraphLines: React.FC = () => (
  <>
    <rect x={3} y={4.125} width={18} height={1.75} rx={0.875} />
    <rect x={9.5} y={8.625} width={11.5} height={1.75} rx={0.875} />
    <rect x={9.5} y={13.625} width={11.5} height={1.75} rx={0.875} />
    <rect x={3} y={18.125} width={18} height={1.75} rx={0.875} />
  </>
);

/** Word "Increase Indent": paragraph lines + a filled right-pointing arrow. */
export const IndentIncreaseIcon: React.FC<IndentIconProps> = ({ fontSize = 20, ...rest }) => (
  <svg width={fontSize} height={fontSize} viewBox={VIEW_BOX} fill="currentColor" {...rest}>
    <ParagraphLines />
    <path d="M3.2 8.8 L3.2 15.2 L7.4 12 Z" />
  </svg>
);

/** Word "Decrease Indent": paragraph lines + a filled left-pointing arrow. */
export const IndentDecreaseIcon: React.FC<IndentIconProps> = ({ fontSize = 20, ...rest }) => (
  <svg width={fontSize} height={fontSize} viewBox={VIEW_BOX} fill="currentColor" {...rest}>
    <ParagraphLines />
    <path d="M7.4 8.8 L7.4 15.2 L3.2 12 Z" />
  </svg>
);
